/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useUserProfileForm.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Custom hook for managing user profile form state and validation.

 🔁 Last Updated: 2025-01-XX by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { validateUserProfile } from '../utils';
import type { UserProfileData, ProfileFormErrors } from '../types';

/**
 * Custom hook for managing user profile form
 */
export function useUserProfileForm(
  formData: UserProfileData,
  setFormData: React.Dispatch<React.SetStateAction<UserProfileData>>
) {
  const { t } = useI18n();
  const [errors, setErrors] = useState<ProfileFormErrors>({});

  const handleInputChange = (field: keyof UserProfileData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors = validateUserProfile(formData, t);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return {
    errors,
    setErrors,
    handleInputChange,
    validateForm
  };
}

